import { useState, useEffect } from "react";
import { InsightData } from "../../types/insightDashboard";

interface UseInsightDataResult {
  data: InsightData | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

const useInsightData = (
  fetchDataFunction: (filters: any) => Promise<InsightData>,
  filters: any,
  enabled: boolean = true,
): UseInsightDataResult => {
  const [data, setData] = useState<InsightData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) return;

    // Filters can change again before a slower request returns, so only the latest request may set state
    let isLatestRequest = true;

    const fetchData = async () => {
      setIsFetching(true);
      setError(null);
      try {
        const result: InsightData = await fetchDataFunction(filters);
        if (!isLatestRequest) return;
        setData(result);
        setIsLoading(false);
      } catch (err: any) {
        if (isLatestRequest) setError(err);
      } finally {
        if (isLatestRequest) setIsFetching(false);
      }
    };

    fetchData();

    return () => {
      isLatestRequest = false;
    };
  }, [fetchDataFunction, filters, enabled]);
  return { data, isLoading, isFetching, error };
};

export default useInsightData;
